import { Mention } from '@tiptap/extension-mention';
import { createMentionSearchHighlightNodeView } from './mention-search-highlight-node-view.js';
import { textIncludesQuery } from './text-search';
function shouldShowScriptMentionDocumentTitle(item, currentDocumentId) {
    const documentTitle = item.documentTitle?.trim();
    if (!documentTitle) {
        return false;
    }
    if (item.label.includes(documentTitle)) {
        return false;
    }
    if (currentDocumentId && item.documentId && item.documentId === currentDocumentId) {
        return false;
    }
    return true;
}
export function buildScriptMentionSuggestionOptions(getScriptMentionItems, getIsScriptModeActive, getPreferEntityMentions, getCurrentDocumentId) {
    return {
        char: '@',
        allowSpaces: true,
        allow: ({ state, range }) => {
            if (getPreferEntityMentions?.()) {
                return false;
            }
            const $from = state.doc.resolve(range.from);
            const type = state.schema.nodes.scriptMention;
            if (!type || !$from.parent.type.contentMatch.matchType(type)) {
                return false;
            }
            return getIsScriptModeActive();
        },
        items: ({ query }) => {
            const entities = getScriptMentionItems();
            return entities
                .filter((e) => textIncludesQuery(e.label ?? '', query) ||
                textIncludesQuery(e.id ?? '', query) ||
                textIncludesQuery(e.documentTitle ?? '', query))
                .slice(0, 20);
        },
        command: (cmdProps) => {
            const { editor, range } = cmdProps;
            const item = cmdProps.props;
            editor
                .chain()
                .focus()
                .insertContentAt(range, [
                {
                    type: 'scriptMention',
                    attrs: {
                        id: item.id,
                        label: item.label,
                        entityType: item.type,
                        documentId: item.documentId ?? null,
                        mentionSuggestionChar: '@',
                    },
                },
            ])
                .run();
        },
        render: () => {
            let list;
            let currentItems = [];
            let currentCommand = () => { };
            const positionList = (props) => {
                const rect = props.clientRect?.();
                if (!rect || !list)
                    return;
                list.style.position = 'fixed';
                list.style.top = `${rect.bottom + 2}px`;
                list.style.left = `${rect.left}px`;
            };
            const updateList = (props) => {
                currentItems = props.items;
                currentCommand = props.command;
                if (!list)
                    return;
                positionList(props);
                list.innerHTML = '';
                props.items.forEach((item, idx) => {
                    const btn = document.createElement('button');
                    btn.type = 'button';
                    btn.className = idx === 0 ? 'mention-item mention-item-selected' : 'mention-item';
                    btn.style.cssText =
                        'display:flex;align-items:center;gap:8px;width:100%;padding:8px 12px;text-align:left;border:none;background:none;cursor:pointer;font-size:14px;color:inherit;';
                    if (idx === 0)
                        btn.style.backgroundColor = 'var(--muted)';
                    const typeSpan = document.createElement('span');
                    typeSpan.style.cssText = 'font-size:11px;opacity:0.7;text-transform:capitalize;flex-shrink:0;';
                    typeSpan.textContent = item.type === 'character' ? 'Char' : 'Loc';
                    btn.appendChild(typeSpan);
                    const labelWrap = document.createElement('span');
                    labelWrap.style.cssText = 'display:flex;flex-direction:column;min-width:0;gap:1px;';
                    const labelSpan = document.createElement('span');
                    labelSpan.textContent = item.label;
                    labelWrap.appendChild(labelSpan);
                    const documentTitle = item.documentTitle?.trim() ?? '';
                    if (documentTitle &&
                        shouldShowScriptMentionDocumentTitle(item, getCurrentDocumentId?.() ?? null)) {
                        const docSpan = document.createElement('span');
                        docSpan.style.cssText =
                            'font-size:11px;opacity:0.7;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;';
                        docSpan.textContent = documentTitle;
                        labelWrap.appendChild(docSpan);
                    }
                    btn.appendChild(labelWrap);
                    btn.addEventListener('mousedown', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        props.command(item);
                    });
                    btn.addEventListener('click', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        props.command(item);
                    });
                    btn.addEventListener('mouseenter', () => {
                        list?.querySelectorAll('.mention-item').forEach((el) => {
                            el.classList.remove('mention-item-selected');
                            el.style.backgroundColor = '';
                        });
                        btn.classList.add('mention-item-selected');
                        btn.style.backgroundColor = 'var(--muted)';
                    });
                    list?.appendChild(btn);
                });
            };
            return {
                onStart: (props) => {
                    list = document.createElement('div');
                    list.className = 'mention-list';
                    list.setAttribute('data-script-mention-list', '');
                    list.style.cssText =
                        'position:fixed;z-index:99999;background:var(--popover);color:var(--popover-foreground);border:1px solid var(--border);border-radius:6px;box-shadow:0 4px 6px -1px rgb(0 0 0/0.1);max-height:200px;overflow-y:auto;min-width:180px;cursor:pointer;pointer-events:auto;';
                    list.addEventListener('wheel', (e) => {
                        if (!list)
                            return;
                        const { scrollTop, scrollHeight, clientHeight } = list;
                        const isScrollable = scrollHeight > clientHeight;
                        const isAtTop = scrollTop <= 0 && e.deltaY < 0;
                        const isAtBottom = scrollTop >= scrollHeight - clientHeight && e.deltaY > 0;
                        if (isScrollable && !isAtTop && !isAtBottom) {
                            e.preventDefault();
                            list.scrollTop += e.deltaY;
                        }
                    }, { passive: false });
                    document.body.appendChild(list);
                    updateList(props);
                },
                onUpdate(props) {
                    updateList(props);
                },
                onKeyDown(props) {
                    if (props.event.key === 'Escape') {
                        props.event.preventDefault();
                        props.event.stopPropagation();
                        list?.remove();
                        return true;
                    }
                    if (!list)
                        return false;
                    const selected = list.querySelector('.mention-item-selected');
                    const itemElements = list.querySelectorAll('.mention-item');
                    const scrollSelectedIntoView = (el) => {
                        el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                    };
                    const setSelected = (el) => {
                        if (!el || !list)
                            return;
                        list.querySelectorAll('.mention-item').forEach((e) => {
                            e.classList.remove('mention-item-selected');
                            e.style.backgroundColor = '';
                        });
                        el.classList.add('mention-item-selected');
                        el.style.backgroundColor = 'var(--muted)';
                        scrollSelectedIntoView(el);
                    };
                    if (props.event.key === 'ArrowDown') {
                        props.event.preventDefault();
                        const nextEl = !selected
                            ? itemElements[0]
                            : selected.nextElementSibling ?? itemElements[0];
                        setSelected(nextEl ?? null);
                        return true;
                    }
                    if (props.event.key === 'ArrowUp') {
                        props.event.preventDefault();
                        const prevEl = !selected
                            ? itemElements[itemElements.length - 1]
                            : selected.previousElementSibling ?? itemElements[itemElements.length - 1];
                        setSelected(prevEl ?? null);
                        return true;
                    }
                    if (props.event.key === 'Enter' && selected) {
                        props.event.preventDefault();
                        const idx = Array.from(itemElements).indexOf(selected);
                        const item = currentItems[idx];
                        if (item)
                            currentCommand(item);
                        return true;
                    }
                    return false;
                },
                onExit() {
                    list?.remove();
                },
            };
        },
    };
}
export const ScriptMention = Mention.extend({
    name: 'scriptMention',
    marks: '_',
    addAttributes() {
        return {
            ...this.parent?.(),
            entityType: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-entity-type'),
                renderHTML: (attributes) => attributes.entityType ? { 'data-entity-type': attributes.entityType } : {},
            },
            documentId: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-document-id'),
                renderHTML: (attributes) => attributes.documentId ? { 'data-document-id': attributes.documentId } : {},
            },
        };
    },
    addNodeView() {
        return createMentionSearchHighlightNodeView();
    },
});
export function configureScriptMention(getScriptMentionItems, getIsScriptModeActive, getPreferEntityMentions, getCurrentDocumentId) {
    return ScriptMention.configure({
        HTMLAttributes: {
            class: 'mention script-mention',
        },
        suggestion: buildScriptMentionSuggestionOptions(getScriptMentionItems, getIsScriptModeActive, getPreferEntityMentions, getCurrentDocumentId),
    });
}
//# sourceMappingURL=script-mention.js.map